import config from './config';
import { HUBSPOT_UTK_MAX_LENGTH } from './types';

/**
 * Gets the subdirectory prefix (e.g., '/TBFE-1955-2' or '').
 * Works both server-side (from config) and client-side (from window variable).
 * @returns The subdirectory prefix (e.g., '/TBFE-1955-2' or '').
 */
export const getSubdirectory = (): string => {
  // Try config.subdirectory first (works server-side and client-side after config loads).
  if (config.subdirectory) {
    return config.subdirectory;
  }

  // Fall back to window variable (client-side, set in index.html).
  if (typeof window !== 'undefined' && (window as any).__TB_SUBDIRECTORY__) {
    return (window as any).__TB_SUBDIRECTORY__;
  }

  return '';
};

/**
 * Gets a static asset path (images, fonts, CSS, JS, etc.) with subdirectory prefix.
 * Handles absolute URLs (http://, https://) by returning them as-is.
 * @param path - The asset path (e.g., '/images/logo.png' or 'images/logo.png').
 * @returns The path with subdirectory prefix if configured.
 */
export const getStaticPath = (path: string): string => {
  // If path is already an absolute URL, return as-is.
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // Ensure path starts with /.
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  let subdirectory = getSubdirectory();

  // Remove trailing slash from subdirectory if present.
  const cleanSubdirectory = subdirectory.endsWith('/') ? subdirectory.slice(0, -1) : subdirectory;

  return cleanSubdirectory ? `${cleanSubdirectory}${normalizedPath}` : normalizedPath;
};

/**
 * Gets a full URL with the subdirectory prefix for window.location.href redirects.
 * Handles absolute URLs (http://, https://) by returning them as-is.
 * @param path - The path (e.g., '/status' or '/apply/customer-service').
 * @returns The full URL with subdirectory prefix if configured.
 */
export const getFullUrl = (path: string): string => {
  return getStaticPath(path);
};

/**
 * Reads HubSpot visitor token from the hubspotutk first-party cookie.
 * Used when POSTing to HubSpot Forms API (context.hutk). The tracking script loads in production only; otherwise this is usually undefined.
 */
export function readHubSpotHutkFromCookie(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const parts = document.cookie.split(';');

  for (const part of parts) {
    const trimmed = part.trim();

    if (!trimmed.startsWith('hubspotutk=')) {
      continue;
    }

    let value = trimmed.slice('hubspotutk='.length);

    try {
      value = decodeURIComponent(value);
    } catch {
      // use raw slice if decode fails
    }

    const normalized = value.trim();

    if (!normalized || normalized.length > HUBSPOT_UTK_MAX_LENGTH) {
      return undefined;
    }

    return normalized;
  }

  return undefined;
}
